import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Edit,
  Trash2,
  Calendar,
  Mail,
  Phone,
  Clock,
  ExternalLink,
  DollarSign,
} from "lucide-react";
import { supabase } from "../lib/supabase";
import { Student, Payment } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import { ENTITY_NAMES } from "../constants/entities";
import SmartBackButton from "../components/ui/SmartBackButton";
import { isRunCompleted } from "../utils/runs";
import {
  getStatusBadgeColor,
  getStatusDisplayName,
  formatPaymentMethod,
  formatPaymentAmount,
  type PaymentStatus,
} from "../utils/payments";

// Extended Payment type that includes run relationship
type PaymentWithRun = Payment & {
  run?: {
    id: string;
    name: string;
    series?: {
      id: string;
      title: string;
      code: string;
    };
  };
};

// Extended Student type that includes run relationship
interface StudentWithRun extends Student {
  run?: {
    id: string;
    name: string;
    day_of_week: string[];
    start_time: string;
    active: boolean;
    lab_dates?: Array<{
      id: string;
      run_id: string;
      lab_number: number;
      date: string;
    }>;
    series?: {
      id: string;
      title: string;
      code: string;
    };
  };
}

// Interface for past enrollments
interface PastEnrollment {
  run_id: string;
  run_name: string;
  series_code: string;
  series_title: string;
  series_id: string;
}

const StudentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [student, setStudent] = useState<StudentWithRun | null>(null);
  const [payments, setPayments] = useState<PaymentWithRun[]>([]);
  const [pastEnrollments, setPastEnrollments] = useState<PastEnrollment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { userRole } = useAuth();
  const navigate = useNavigate();

  const isAdmin = userRole === "admin";

  useEffect(() => {
    const fetchStudent = async () => {
      try {
        setIsLoading(true);

        if (!id) return;

        // Fetch student details
        const { data, error } = await supabase
          .from("students")
          .select(
            `*, 
            notes,
            run:run_id (
              id, 
              name, 
              day_of_week, 
              start_time, 
              active,
              lab_dates (
                id,
                run_id,
                lab_number,
                date
              ),
              series:series_id (
                id, 
                title, 
                code
              )
            )`
          )
          .eq("id", id)
          .single();

        if (error) {
          console.error("Supabase error details:", error);
          throw error;
        }

        console.log("Student data from DB:", data);

        if (data) {
          setStudent(data);
        } else {
          setStudent(null);
        }

        // Fetch student's payments
        const { data: paymentsData, error: paymentsError } = await supabase
          .from("payments")
          .select(
            `
            *,
            run:run_id (
              id,
              name,
              series:series_id (
                id,
                title,
                code
              )
            )
          `
          )
          .eq("student_id", id)
          .order("payment_date", { ascending: false })
          .limit(5);

        if (paymentsError) {
          console.error("Error fetching payments:", paymentsError);
        } else {
          setPayments(paymentsData || []);
        }

        // Fetch past enrollments (from enrollments table if exists, or we can use a different approach)
        await fetchPastEnrollments(id);
      } catch (error) {
        console.error("Error fetching student:", error);
        toast.error("Failed to load student details");
      } finally {
        setIsLoading(false);
      }
    };

    fetchStudent();
  }, [id]);

  const fetchPastEnrollments = async (studentId: string) => {
    try {
      // Get payment history to find past runs student was enrolled in
      // This is a simpler approach since we have payment records tied to students
      const { data: paymentHistory, error } = await supabase
        .from("payments")
        .select(
          `
          *,
          students!inner (
            run_id,
            run:run_id (
              id,
              name,
              active,
              lab_dates (
                id,
                run_id,
                lab_number,
                date
              ),
              series:series_id (
                id,
                title,
                code
              )
            )
          )
        `
        )
        .eq("students.id", studentId);

      if (error) {
        console.error("Error fetching past enrollments:", error);
        return;
      }

      // Create a set to avoid duplicates and filter for completed runs
      const seenRuns = new Set<string>();
      const pastEnrollmentsData: PastEnrollment[] = [];

      (paymentHistory || []).forEach((payment) => {
        const student = payment.students;
        if (student?.run && !seenRuns.has(student.run.id)) {
          seenRuns.add(student.run.id);

          // Check if this run is completed
          if (
            isRunCompleted({
              active: student.run.active,
              lab_dates: student.run.lab_dates || [],
            })
          ) {
            pastEnrollmentsData.push({
              run_id: student.run.id,
              run_name: student.run.name,
              series_code: student.run.series?.code || "",
              series_title: student.run.series?.title || "",
              series_id: student.run.series?.id || "",
            });
          }
        }
      });

      setPastEnrollments(pastEnrollmentsData);
    } catch (error) {
      console.error("Error fetching past enrollments:", error);
    }
  };

  const handleDeleteStudent = async () => {
    if (!student || !isAdmin) return;

    if (window.confirm("Are you sure you want to delete this student?")) {
      try {
        const { error } = await supabase
          .from("students")
          .delete()
          .eq("id", student.id);

        if (error) throw error;

        toast.success("Student deleted successfully");
        navigate("/students");
      } catch (error) {
        console.error("Error deleting student:", error);
        toast.error("Failed to delete student");
      }
    }
  };

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="flex items-center mb-6">
          <div className="w-24 h-8 bg-neutral-200 rounded"></div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6 border border-neutral-200">
          <div className="w-48 h-8 bg-neutral-200 rounded mb-4"></div>
          <div className="w-full h-64 bg-neutral-100 rounded"></div>
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="text-center py-12">
        <h2 className="mb-4">Student Not Found</h2>
        <p className="mb-6">
          The student you are looking for does not exist or has been removed.
        </p>
        <SmartBackButton fallbackPath="/students" className="btn btn-primary" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center mb-6">
        <SmartBackButton fallbackPath="/students" />
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-neutral-200">
        <div className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <h1 className="">{student.codername}</h1>
            <div className="mt-4 sm:mt-0 flex space-x-2">
              <Link
                to={`/students/${student.id}/edit`}
                className="btn btn-edit"
              >
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </Link>
              {isAdmin && (
                <button
                  onClick={handleDeleteStudent}
                  className="btn btn-danger"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-6">
              <div>
                <h2 className="mb-4">Student Information</h2>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="bg-primary-100 rounded-md p-2 text-primary-800">
                      <Clock className="w-5" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium">Current Enrollment</p>
                      {student.run ? (
                        <>
                          {/* Check if current run is completed */}
                          {isRunCompleted({
                            active: student.run.active,
                            lab_dates: student.run.lab_dates || [],
                          }) ? (
                            <div>
                              <p className="text-neutral-500">
                                Not currently enrolled in an active group
                              </p>
                            </div>
                          ) : (
                            <p className="">
                              <Link
                                to={`/runs/${student.run.id}`}
                                className="text-link"
                              >
                                {ENTITY_NAMES.RUN} {student.run.name}
                              </Link>{" "}
                              of{" "}
                              <Link
                                to={`/series/${student.run.series?.id}`}
                                className="text-link"
                              >
                                {student.run.series?.code}
                              </Link>
                            </p>
                          )}
                        </>
                      ) : (
                        <span className="text-neutral-400">Not enrolled</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-primary-100 rounded-md p-2 text-primary-800">
                      <Calendar className="w-5" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium">Age</p>
                      <p className="">{student.age} years old</p>
                    </div>
                  </div>

                  {student.school && (
                    <div className="flex items-start">
                      <div className="bg-primary-100 rounded-md p-2 text-primary-800">
                        <svg
                          className="w-5"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m4 0v-5a1 1 0 011-1h4a1 1 0 011 1v5M9 7h6M9 11h6"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium">School</p>
                        <p className="">{student.school}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <h2 className="text-xl2 text-main mb-4">Parent Information</h2>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="bg-primary-100 rounded-md p-2 text-primary-800">
                      <svg
                        className="w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium">Parent Name</p>
                      <p className="">{student.parentname}</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="bg-primary-100 rounded-md p-2 text-primary-800">
                      <Mail className="w-5" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium">Email</p>
                      <a
                        href={`mailto:${student.email}`}
                        className="text-link hover:text-hover"
                      >
                        {student.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="bg-primary-100 rounded-md p-2 text-primary-800">
                      <Phone className="w-5" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium">Phone</p>
                      <a
                        href={`tel:${student.phone}`}
                        className="text-link hover:text-hover"
                      >
                        {student.phone}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl2 text-main">Payments</h2>
            </div>

            {payments.length > 0 ? (
              <div className="bg-white shadow-sm rounded-lg border border-neutral-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-neutral-200">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Payment ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          For
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Method
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-neutral-200">
                      {payments.map((payment) => {
                        const status = payment.status as PaymentStatus;
                        const formatPaymentId = (
                          payment: PaymentWithRun
                        ): string => {
                          if (payment.stripe_payment_intent_id) {
                            return payment.stripe_payment_intent_id;
                          }
                          if (payment.stripe_session_id) {
                            return (
                              payment.stripe_session_id.substring(0, 16) + "..."
                            );
                          }
                          return payment.id.substring(0, 8).toUpperCase();
                        };

                        return (
                          <tr key={payment.id} className="hover:bg-neutral-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Link
                                to={`/payments/${payment.id}`}
                                className="text-sm font-medium text-link hover:text-hover"
                              >
                                {formatPaymentId(payment)}
                              </Link>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm">
                                {payment.run ? (
                                  <>
                                    <Link
                                      to={`/runs/${payment.run.id}`}
                                      className="text-link hover:text-hover"
                                    >
                                      {ENTITY_NAMES.RUN} {payment.run.name}
                                    </Link>
                                    {payment.run.series && (
                                      <>
                                        {" "}
                                        of{" "}
                                        <Link
                                          to={`/series/${payment.run.series.id}`}
                                          className="text-link hover:text-hover"
                                        >
                                          {payment.run.series.code}
                                        </Link>
                                      </>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-gray-500">
                                    No Group
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-neutral-900">
                                {formatPaymentAmount(payment.amount)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm ${getStatusBadgeColor(
                                  status
                                )}`}
                              >
                                {getStatusDisplayName(status)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-neutral-900">
                                {status === "not_received"
                                  ? "None"
                                  : new Date(
                                      payment.payment_date
                                    ).toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "short",
                                      day: "numeric",
                                    })}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-neutral-900">
                                {formatPaymentMethod(
                                  payment.payment_method || "",
                                  status
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end space-x-2">
                                <Link
                                  to={`/payments/${payment.id}`}
                                  className="icon-view"
                                  title="View payment details"
                                >
                                  <ExternalLink className="h-5 w-5" />
                                </Link>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="border-t border-neutral-200 bg-neutral-50">
                  <div className="flex justify-between text-sm">
                    <span className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Total paid:
                    </span>
                    <span className="px-6 py-3 text-right font-medium">
                      {formatPaymentAmount(
                        payments
                          .filter((p) => p.status === "completed")
                          .reduce((sum, p) => sum + p.amount, 0)
                      )}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-neutral-50 rounded-lg p-4 text-center">
                <DollarSign className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                <p className="text-sm text-gray-500">
                  No payments recorded yet.
                </p>
              </div>
            )}
          </div>

          {/* Past Groups Section */}
          <div className="mt-8">
            <h2 className="text-xl2 text-main mb-4">History</h2>
            {pastEnrollments.length > 0 ? (
              <div className="bg-white shadow-sm rounded-lg border border-neutral-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-neutral-200">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Group
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Series
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-neutral-200">
                      {pastEnrollments.map((enrollment, index) => (
                        <tr
                          key={`${enrollment.run_id}-${index}`}
                          className="hover:bg-neutral-50"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium">
                              <Link
                                to={`/runs/${enrollment.run_id}`}
                                className="text-link hover:text-hover"
                              >
                                {ENTITY_NAMES.RUN} {enrollment.run_name}
                              </Link>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm">
                              <Link
                                to={`/series/${enrollment.series_id}`}
                                className="text-link hover:text-hover"
                              >
                                {enrollment.series_code}:{" "}
                                {enrollment.series_title}
                              </Link>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="bg-neutral-50 rounded-lg p-4 text-center">
                <Calendar className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                <p className="text-sm text-gray-500">
                  No completed groups found.
                </p>
              </div>
            )}
          </div>

          <div className="mt-8">
            <h2 className="text-xl2 text-main mb-4">Notes</h2>
            <div className="bg-white shadow-sm rounded-lg border border-neutral-200 p-6">
              {student.notes ? (
                <div className="prose max-w-none">
                  <div className="whitespace-pre-wrap text-neutral-700">
                    {student.notes}
                  </div>
                  {isAdmin && (
                    <div className="mt-4">
                      <Link
                        to={`/students/${student.id}/edit`}
                        className="btn btn-edit"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit Notes
                      </Link>
                    </div>
                  )}
                </div>
              ) : (
                <div className="">
                  <p className="text-neutral-500 mb-4">No notes added yet.</p>
                  {isAdmin && (
                    <Link
                      to={`/students/${student.id}/edit`}
                      className="btn btn-primary"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Add Notes
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDetail;
